import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { notificationService } from '../services/notification.service';
import { signalRService } from '../services/signalr.service';
import { useAuth } from './AuthContext';

interface NotificationContextType {
  isServiceWorkerReady: boolean;
  isSubscribed: boolean;
  isSignalRConnected: boolean;
  notificationPermission: NotificationPermission;
  requestNotificationPermission: () => Promise<NotificationPermission>;
  subscribeToNotifications: () => Promise<boolean>;
  unsubscribeFromNotifications: () => Promise<boolean>;
  connectSignalR: () => Promise<boolean>;
  disconnectSignalR: () => Promise<void>;
  onNotification: (callback: (data: any) => void) => void;
  offNotification: (callback: (data: any) => void) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications debe ser usado dentro de NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider = ({ children }: NotificationProviderProps) => {
  const { state } = useAuth();
  const token = state.accessToken;
  const isAuthenticated = state.isAuthenticated;
  const [isServiceWorkerReady, setIsServiceWorkerReady] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSignalRConnected, setIsSignalRConnected] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission>(
    notificationService.getPermission()
  );

  useEffect(() => {
    const initializeServiceWorker = async () => {
      const initialized = await notificationService.initialize();
      setIsServiceWorkerReady(initialized);

      if (initialized) {
        const subscription = await notificationService.getSubscription();
        setIsSubscribed(subscription !== null);
      }
    };

    initializeServiceWorker();
  }, []);

  useEffect(() => {
    if (isAuthenticated && token && isServiceWorkerReady && !isSignalRConnected) {
      // Auto-conectar SignalR cuando el usuario está autenticado
      console.log('[NotificationContext] Conectando SignalR con token...');
      connectSignalR();
    } else if (!isAuthenticated && isSignalRConnected) {
      // Solo desconectar SignalR, NO desuscribir push notifications
      console.log('[NotificationContext] Desconectando SignalR al cerrar sesión...');
      disconnectSignalR();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, token, isServiceWorkerReady, isSignalRConnected]);

  // Verificar y sincronizar suscripción existente al iniciar sesión
  useEffect(() => {
    const checkExistingSubscription = async () => {
      if (isAuthenticated && token && isServiceWorkerReady) {
        // Verificar si ya existe una suscripción en el navegador
        const subscription = await notificationService.getSubscription();

        if (subscription && !isSubscribed) {
          // Existe una suscripción pero el estado no está actualizado
          console.log('[NotificationContext] Suscripción existente detectada, sincronizando con backend...');

          // Actualizar estado local
          setIsSubscribed(true);

          // Sincronizar con el backend (actualizar idUsuario asociado)
          try {
            await notificationService.subscribeToPushNotifications(token);
            console.log('[NotificationContext] Suscripción sincronizada con backend');
          } catch (error) {
            console.error('[NotificationContext] Error sincronizando suscripción:', error);
          }
        } else if (subscription) {
          // La suscripción existe y el estado está correcto
          setIsSubscribed(true);
        } else {
          // No hay suscripción
          setIsSubscribed(false);
        }
      }
    };

    checkExistingSubscription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, token, isServiceWorkerReady]);

  const requestNotificationPermission = async (): Promise<NotificationPermission> => {
    const permission = await notificationService.requestPermission();
    setNotificationPermission(permission);

    // Si se otorgan permisos, suscribir automáticamente
    if (permission === 'granted' && token && !isSubscribed) {
      console.log('[NotificationContext] Permisos otorgados, suscribiendo automáticamente...');
      await subscribeToNotifications();
    }

    return permission;
  };

  const subscribeToNotifications = async (): Promise<boolean> => {
    if (!token) {
      console.warn('No hay token de autenticación disponible');
      return false;
    }

    const subscription = await notificationService.subscribeToPushNotifications(token);
    const success = subscription !== null;
    setIsSubscribed(success);

    if (success) {
      console.log('Suscripción a notificaciones exitosa');
    }

    return success;
  };

  const unsubscribeFromNotifications = async (): Promise<boolean> => {
    if (!token) {
      console.warn('No hay token de autenticación disponible');
      return false;
    }

    const success = await notificationService.unsubscribeFromPushNotifications(token);
    if (success) {
      setIsSubscribed(false);
      console.log('Desuscripción de notificaciones exitosa');
    }

    return success;
  };

  const connectSignalR = async (): Promise<boolean> => {
    if (!token) {
      console.warn('No hay token de autenticación disponible para SignalR');
      return false;
    }

    const connected = await signalRService.connect(token);
    setIsSignalRConnected(connected);

    if (connected) {
      console.log('SignalR conectado exitosamente');
      
      // Configurar reconexión automática
      signalRService.on('connectionClosed', () => {
        setIsSignalRConnected(false);
      });

      signalRService.on('reconnected', () => {
        setIsSignalRConnected(true);
      });
    }

    return connected;
  };

  const disconnectSignalR = async (): Promise<void> => {
    await signalRService.disconnect();
    setIsSignalRConnected(false);
  };

  const onNotification = (callback: (data: any) => void) => {
    signalRService.on('notification', callback);
  };

  const offNotification = (callback: (data: any) => void) => {
    signalRService.off('notification', callback);
  };

  const value: NotificationContextType = {
    isServiceWorkerReady,
    isSubscribed,
    isSignalRConnected,
    notificationPermission,
    requestNotificationPermission,
    subscribeToNotifications,
    unsubscribeFromNotifications,
    connectSignalR,
    disconnectSignalR,
    onNotification,
    offNotification
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
